import { GTG_KENYA_COUNTIES } from "../constants/gtgGeoConstants";

/**
 * Normalized key for matching user/Google Ads–style county strings to canonical GTG names
 * (hyphens, spaces, apostrophes, optional "County" suffix).
 * @param {string} raw
 * @returns {string}
 */
export function normalizeKenyaCountyCompareKey(raw) {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/\s+County$/i, "")
    .trim()
    .replace(/-/g, " ")
    .replace(/'/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Resolve a single token to a canonical county name, or null.
 * Accepts exact GTG spellings, "Nakuru County", Google "Muranga County" → `Murang'a`, etc.
 * @param {string} raw
 * @param {readonly string[]} [countyList]
 * @returns {string|null}
 */
export function resolveKenyaCountyCanonical(raw, countyList = GTG_KENYA_COUNTIES) {
  if (typeof raw !== "string" || !raw.trim()) return null;
  const key = normalizeKenyaCountyCompareKey(raw);
  if (!key) return null;
  const exactLower = new Map(countyList.map((c) => [String(c).toLowerCase(), c]));
  const direct = exactLower.get(String(raw).trim().toLowerCase());
  if (direct) return direct;
  for (const c of countyList) {
    if (normalizeKenyaCountyCompareKey(c) === key) return c;
  }
  return null;
}

/**
 * Parse multiline / comma-separated Kenya county input against a canonical list.
 * @param {string} text
 * @param {readonly string[]} [countyList]
 * @returns {{ counties: string[], unknown: string[] }}
 */
export function parseKenyaCountiesText(text, countyList = GTG_KENYA_COUNTIES) {
  if (typeof text !== "string" || !text.trim()) {
    return { counties: [], unknown: [] };
  }
  const parts = text
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const counties = [];
  const unknown = [];
  const seen = new Set();
  for (const p of parts) {
    const canon = resolveKenyaCountyCanonical(p, countyList);
    if (canon) {
      if (!seen.has(canon)) {
        seen.add(canon);
        counties.push(canon);
      }
    } else {
      unknown.push(p);
    }
  }
  return { counties, unknown };
}
